/**
 * Alta de cuenta (§1.a).
 *
 * Las reglas de correo y contraseña se comprueban también aquí, mientras se
 * escribe, aunque la autoridad sea el backend: enterarse de que la
 * contraseña no sirve después de rellenar ocho campos es la peor forma de
 * descubrirlo.
 */

import { Check, Network, UserPlus, X } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { ErrorApi } from '../api/cliente'
import { useRegistrarse } from '../api/sesion'
import type { DatosRegistro } from '../api/tipos'
import { PAISES } from '../ui/paises'
import { Boton, Campo, Entrada, Panel, Seleccion } from '../ui/primitivos'
import { cx } from '../ui/util'
import { esCorreoPersonal, reglasPassword } from '../ui/registro-reglas'

const VACIO: DatosRegistro = {
  nombre: '',
  apellido: '',
  email: '',
  empresa: '',
  cargo: '',
  telefono_pais: 'CL',
  telefono_numero: '',
  password: '',
  password2: '',
}

export function Registro() {
  const registrarse = useRegistrarse()
  const [datos, setDatos] = useState<DatosRegistro>(VACIO)

  const cambiar = (campo: keyof DatosRegistro) => (v: string) =>
    setDatos((d) => ({ ...d, [campo]: v }))

  const correoPersonal = esCorreoPersonal(datos.email)
  const reglas = reglasPassword(datos.password)
  const passwordOk = reglas.every((r) => r.cumple)
  const coinciden = datos.password !== '' && datos.password === datos.password2

  const completo =
    datos.nombre.trim() !== '' &&
    datos.apellido.trim() !== '' &&
    datos.email.trim() !== '' &&
    datos.empresa.trim() !== '' &&
    datos.cargo.trim() !== '' &&
    datos.telefono_numero.trim() !== '' &&
    passwordOk &&
    coinciden &&
    !correoPersonal

  const error = registrarse.error instanceof ErrorApi ? registrarse.error : undefined
  const porCampo = error?.errors ?? {}

  if (registrarse.isSuccess) {
    return (
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center gap-4 p-4">
        <Panel titulo="Revisa tu correo">
          <div className="grid gap-3 p-4 text-[13px]">
            <p>{registrarse.data.detail}</p>
            <p className="text-[var(--color-texto-suave)]">
              El enlace activa la cuenta y te trae de vuelta aquí. Si no llega en unos minutos,
              mira la carpeta de correo no deseado.
            </p>
            <Link to="/login">
              <Boton variante="secundario">Ir a entrar</Boton>
            </Link>
          </div>
        </Panel>
      </div>
    )
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-xl flex-col justify-center gap-4 p-4">
      <div className="flex items-center justify-center gap-2">
        <Network size={20} className="text-[var(--color-acento)]" />
        <span className="text-[18px] font-semibold tracking-tight">StakeMap</span>
      </div>

      <Panel titulo="Crear una cuenta">
        <form
          className="grid gap-3 p-3"
          onSubmit={(e) => {
            e.preventDefault()
            registrarse.mutate(datos)
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo etiqueta="Nombre">
              <Entrada
                autoFocus
                required
                value={datos.nombre}
                onChange={(e) => cambiar('nombre')(e.target.value)}
              />
            </Campo>
            <Campo etiqueta="Apellido">
              <Entrada
                required
                value={datos.apellido}
                onChange={(e) => cambiar('apellido')(e.target.value)}
              />
            </Campo>
          </div>

          <Campo
            etiqueta="Correo corporativo"
            ayuda="No se admiten gmail, outlook, yahoo ni similares."
          >
            <Entrada
              required
              type="email"
              autoComplete="username"
              placeholder="tu.nombre@tuempresa.cl"
              value={datos.email}
              aria-invalid={correoPersonal || undefined}
              onChange={(e) => cambiar('email')(e.target.value)}
            />
            {correoPersonal && (
              <p className="mt-1 text-[12px] text-[var(--color-opositora)]">
                Ese es un correo personal. Usa el de tu empresa o institución.
              </p>
            )}
            <ErroresDeCampo mensajes={porCampo.email} />
          </Campo>

          <div className="grid gap-3 sm:grid-cols-2">
            <Campo etiqueta="Empresa">
              <Entrada
                required
                value={datos.empresa}
                onChange={(e) => cambiar('empresa')(e.target.value)}
              />
            </Campo>
            <Campo etiqueta="Cargo">
              <Entrada
                required
                value={datos.cargo}
                onChange={(e) => cambiar('cargo')(e.target.value)}
              />
            </Campo>
          </div>

          <Campo etiqueta="Teléfono">
            <div className="flex gap-2">
              <Seleccion
                className="w-44 shrink-0"
                value={datos.telefono_pais}
                onChange={(e) => cambiar('telefono_pais')(e.target.value)}
              >
                {PAISES.map((p) => (
                  <option key={p.iso} value={p.iso}>
                    {p.nombre} ({p.prefijo})
                  </option>
                ))}
              </Seleccion>
              <Entrada
                required
                inputMode="tel"
                placeholder="912345678"
                className="min-w-0 flex-1"
                value={datos.telefono_numero}
                onChange={(e) => cambiar('telefono_numero')(e.target.value)}
              />
            </div>
            <ErroresDeCampo mensajes={porCampo.telefono_numero} />
          </Campo>

          <Campo etiqueta="Contraseña">
            <Entrada
              required
              type="password"
              autoComplete="new-password"
              value={datos.password}
              onChange={(e) => cambiar('password')(e.target.value)}
            />
            {datos.password !== '' && (
              <ul className="mt-1.5 grid gap-0.5 sm:grid-cols-2">
                {reglas.map((r) => (
                  <li
                    key={r.texto}
                    className={cx(
                      'flex items-center gap-1 text-[11px]',
                      r.cumple
                        ? 'text-[var(--color-favorable)]'
                        : 'text-[var(--color-texto-tenue)]',
                    )}
                  >
                    {r.cumple ? <Check size={11} /> : <X size={11} />}
                    {r.texto}
                  </li>
                ))}
              </ul>
            )}
            <ErroresDeCampo mensajes={porCampo.password} />
          </Campo>

          <Campo etiqueta="Repite la contraseña">
            <Entrada
              required
              type="password"
              autoComplete="new-password"
              value={datos.password2}
              aria-invalid={(datos.password2 !== '' && !coinciden) || undefined}
              onChange={(e) => cambiar('password2')(e.target.value)}
            />
            {datos.password2 !== '' && !coinciden && (
              <p className="mt-1 text-[12px] text-[var(--color-opositora)]">
                Las dos contraseñas no coinciden.
              </p>
            )}
            <ErroresDeCampo mensajes={porCampo.password2} />
          </Campo>

          {error && !Object.keys(porCampo).length && (
            <p className="text-[12px] text-[var(--color-opositora)]">{error.message}</p>
          )}

          <Boton
            type="submit"
            variante="primario"
            icono={<UserPlus size={14} />}
            cargando={registrarse.isPending}
            disabled={!completo}
          >
            Crear cuenta
          </Boton>
        </form>
      </Panel>

      <p className="text-center text-[13px] text-[var(--color-texto-suave)]">
        ¿Ya tienes cuenta?{' '}
        <Link to="/login" className="text-[var(--color-acento)] underline">
          Entra
        </Link>
      </p>
    </div>
  )
}

function ErroresDeCampo({ mensajes }: { mensajes?: string[] }) {
  if (!mensajes?.length) return null
  return <p className="mt-1 text-[12px] text-[var(--color-opositora)]">{mensajes.join(' ')}</p>
}
